import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLive } from "@/lib/live";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus, Trash, ListChecks, CheckCircle, Circle, Clock,
  UserCircle, Fire, Warning, Calendar, PencilSimple, Paperclip,
  FilePdf, ImageSquare, X as XIcon, Eye, DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PRIO_META = {
  high:   { label: "HIGH",   color: "text-red-300 border-red-500/50 bg-red-500/10",   glow: "shadow-[0_0_18px_rgba(239,68,68,0.15)]" },
  medium: { label: "MEDIUM", color: "text-amber-200 border-amber-500/50 bg-amber-500/10", glow: "" },
  low:    { label: "LOW",    color: "text-slate-300 border-slate-500/50 bg-slate-500/10",  glow: "" },
};

const isOverdue = (task) => {
  if (!task.due_date || task.status === "done") return false;
  const d = new Date(task.due_date + "T23:59:59");
  return d < new Date();
};

const fmtDue = (dueStr) => {
  if (!dueStr) return "";
  try {
    return new Date(dueStr + "T00:00:00").toLocaleDateString("en-IN", {
      day: "2-digit", month: "short",
    });
  } catch (_e) { return dueStr; }
};

export default function TasksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "secondary_admin";
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("all");   // all | mine | unassigned
  const [statusFilter, setStatusFilter] = useState("pending"); // pending | done | ""
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useLive(() => { load(); });
  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("scope", scope);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/tasks?${params.toString()}`);
      setTasks(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, statusFilter]);

  const toggleDone = async (t) => {
    try {
      const next = t.status === "done" ? "pending" : "done";
      await api.patch(`/tasks/${t.id}`, { status: next });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removeTask = async (t) => {
    if (!window.confirm(`Delete task: "${t.title}"?`)) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Task deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="tasks-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// TO-DO LIST</div>
          <h1 className="font-heading text-3xl font-black text-white">Tasks</h1>
          <p className="text-slate-400 text-sm mt-1">
            Ek jagah saare kaam. Kisi bhi staff ko assign karo — unke panel me highlight ho jaayega.
          </p>
        </div>
        <Button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="btn-industrial rounded-sm h-11 px-5 font-heading tracking-widest"
          data-testid="new-task-btn"
        >
          <Plus size={16} className="mr-1" weight="bold" /> NEW TASK
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {[
          { k: "all",        label: "All" },
          { k: "mine",       label: "Assigned to Me" },
          { k: "unassigned", label: "Unassigned" },
          { k: "created_by_me", label: "Created by Me" },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setScope(s.k)}
            data-testid={`tasks-scope-${s.k}`}
            className={
              "px-3 h-9 rounded-sm text-xs font-mono-num tracking-widest transition " +
              (scope === s.k
                ? "bg-orange-500 text-white"
                : "bg-slate-800/60 text-slate-300 hover:bg-slate-800 border border-slate-700")
            }
          >
            {s.label.toUpperCase()}
          </button>
        ))}
        <div className="ml-auto flex gap-2 items-center text-xs">
          <span className="font-mono-num tracking-widest text-slate-500">STATUS</span>
          {[
            { k: "pending", label: "Pending" },
            { k: "done", label: "Done" },
            { k: "", label: "All" },
          ].map((s) => (
            <button
              key={s.k}
              onClick={() => setStatusFilter(s.k)}
              data-testid={`tasks-status-${s.k || 'both'}`}
              className={
                "px-2.5 h-8 rounded-sm text-[11px] font-mono-num tracking-widest transition " +
                (statusFilter === s.k
                  ? "bg-cyan-500 text-slate-900"
                  : "bg-slate-800/60 text-slate-300 hover:bg-slate-800 border border-slate-700")
              }
            >
              {s.label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-500">Loading…</div>
      ) : tasks.length === 0 ? (
        <div className="surface rounded-sm p-10 text-center text-slate-500">
          <ListChecks size={40} className="mx-auto mb-3 text-slate-700" />
          Koi task nahi. Tap <b className="text-orange-400">+ NEW TASK</b> to add one.
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              currentUser={user}
              onToggleDone={() => toggleDone(t)}
              onEdit={isAdmin || t.created_by === user?.email ? () => { setEditing(t); setFormOpen(true); } : null}
              onDelete={isAdmin || t.created_by === user?.email ? () => removeTask(t) : null}
            />
          ))}
        </div>
      )}

      <TaskFormDialog
        open={formOpen}
        editing={editing}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={() => { setFormOpen(false); setEditing(null); load(); }}
      />
    </div>
  );
}

function TaskRow({ task, currentUser, onToggleDone, onEdit, onDelete }) {
  const done = task.status === "done";
  const overdue = isOverdue(task);
  const prio = PRIO_META[task.priority] || PRIO_META.medium;
  const mine = task.assigned_to_user_id === currentUser?._id;

  return (
    <div
      data-testid={`task-row-${task.id}`}
      className={
        "surface rounded-sm p-3 flex items-start gap-3 transition-all " +
        (done ? "opacity-60 " : "") +
        (mine && !done ? "border-l-4 border-l-orange-500 " : "") +
        (overdue ? "ring-1 ring-red-500/30 " : "") +
        prio.glow
      }
    >
      <button
        onClick={onToggleDone}
        className="mt-1 shrink-0 text-slate-400 hover:text-emerald-400 transition"
        data-testid={`task-toggle-${task.id}`}
        title={done ? "Reopen" : "Mark as Done"}
      >
        {done
          ? <CheckCircle size={22} weight="fill" className="text-emerald-400" />
          : <Circle size={22} />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={"text-sm font-semibold " + (done ? "line-through text-slate-400" : "text-white")}>
            {task.title}
          </div>
          <span className={`text-[10px] font-mono-num tracking-widest border px-1.5 py-0.5 rounded-sm ${prio.color}`}>
            {prio.label}
          </span>
          {overdue && (
            <span className="text-[10px] font-mono-num tracking-widest text-red-300 border border-red-500/50 bg-red-500/10 px-1.5 py-0.5 rounded-sm flex items-center gap-1">
              <Warning size={10} weight="fill" /> OVERDUE
            </span>
          )}
          {mine && !done && (
            <span className="text-[10px] font-mono-num tracking-widest text-orange-300 border border-orange-500/50 bg-orange-500/15 px-1.5 py-0.5 rounded-sm">
              MY TASK
            </span>
          )}
        </div>
        {task.description && (
          <div className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">{task.description}</div>
        )}
        <div className="flex items-center gap-3 flex-wrap mt-1.5 text-[11px] font-mono-num text-slate-500">
          {task.assignee ? (
            <span className="flex items-center gap-1 text-cyan-300">
              <UserCircle size={12} weight="fill" />
              {task.assignee.name}
              <span className="text-slate-600">·</span>
              <span className="text-slate-500">{(task.assignee.role || "").replace(/_/g, " ").toUpperCase()}</span>
            </span>
          ) : (
            <span className="flex items-center gap-1 text-slate-500">
              <UserCircle size={12} />
              Unassigned
            </span>
          )}
          {task.due_date && (
            <span className={"flex items-center gap-1 " + (overdue ? "text-red-300" : "")}>
              <Calendar size={12} /> {fmtDue(task.due_date)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock size={12} /> {task.created_by_name || task.created_by}
          </span>
          {(task.attachments?.length || 0) > 0 && (
            <span
              className="flex items-center gap-1 text-cyan-300"
              data-testid={`task-attach-count-${task.id}`}
            >
              <Paperclip size={12} weight="bold" /> {task.attachments.length}
            </span>
          )}
          {done && task.completed_by_name && (
            <span className="text-emerald-400">✓ Done by {task.completed_by_name}</span>
          )}
        </div>
      </div>

      <div className="flex gap-1 items-center shrink-0">
        {onEdit && (
          <Button
            onClick={onEdit}
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid={`task-edit-${task.id}`}
            title="Edit"
          >
            <PencilSimple size={13} />
          </Button>
        )}
        {onDelete && (
          <Button
            onClick={onDelete}
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 border-red-500/40 text-red-400 hover:bg-red-500/10"
            data-testid={`task-delete-${task.id}`}
            title="Delete"
          >
            <Trash size={13} />
          </Button>
        )}
      </div>
    </div>
  );
}

function TaskFormDialog({ open, editing, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "secondary_admin";
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  const [assigneeId, setAssigneeId] = useState("__none__");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  // Attachments: `queued` = picked but not yet uploaded (only for NEW task,
  // uploaded after task is saved). `existing` = already on the task in edit
  // mode, streamed from the server.
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [existing, setExisting] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/tasks/assignable/users");
        setUsers(data);
      } catch (_e) { /* ignore */ }
    })();
    if (editing) {
      setTitle(editing.title || "");
      setDescription(editing.description || "");
      setPriority(editing.priority || "medium");
      setDueDate(editing.due_date || "");
      setAssigneeId(editing.assigned_to_user_id || "__none__");
      setExisting(editing.attachments || []);
    } else {
      setTitle("");
      setDescription("");
      setPriority("medium");
      setDueDate("");
      setAssigneeId(isAdmin ? "__none__" : (user?._id || "__none__"));
      setExisting([]);
    }
    setQueuedFiles([]);
  }, [open, editing, user, isAdmin]);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowed = /^(image\/(jpeg|jpg|png|webp|heic|heif|gif)|application\/pdf)$/i;
    const good = files.filter((f) => {
      if (!allowed.test(f.type)) {
        toast.error(`${f.name}: sirf photos ya PDF allowed`);
        return false;
      }
      if (f.size > 15 * 1024 * 1024) {
        toast.error(`${f.name}: 15 MB se bada`);
        return false;
      }
      return true;
    });
    if (!good.length) { e.target.value = ""; return; }
    if (editing) {
      // Edit mode → upload immediately
      uploadToTask(editing.id, good);
    } else {
      // New mode → queue for post-save upload
      setQueuedFiles((prev) => [...prev, ...good]);
    }
    e.target.value = "";
  };

  const uploadToTask = async (tid, files) => {
    setUploadingCount(files.length);
    let uploaded = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/tasks/${tid}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setExisting((prev) => [...prev, data]);
        uploaded += 1;
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err.response?.data?.detail) || err.message}`);
      } finally {
        setUploadingCount((c) => Math.max(0, c - 1));
      }
    }
    if (uploaded > 0) toast.success(`${uploaded} file${uploaded > 1 ? "s" : ""} attached`);
  };

  const removeExisting = async (att) => {
    if (!editing) return;
    if (!window.confirm(`Remove ${att.original_filename}?`)) return;
    try {
      await api.delete(`/tasks/${editing.id}/attachments/${att.id}`);
      setExisting((prev) => prev.filter((a) => a.id !== att.id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const removeQueued = (idx) => {
    setQueuedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        priority,
        due_date: dueDate || null,
        assigned_to_user_id: assigneeId === "__none__" ? null : assigneeId,
      };
      if (editing) {
        await api.patch(`/tasks/${editing.id}`, body);
        toast.success("Task updated");
        onSaved?.();
      } else {
        const { data: created } = await api.post("/tasks", body);
        if (queuedFiles.length) {
          await uploadToTask(created.id, queuedFiles);
        }
        toast.success("Task added");
        onSaved?.();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-lg bg-slate-900 border-slate-700 text-white rounded-sm max-h-[92vh] overflow-y-auto"
        data-testid="task-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading tracking-widest">
            <ListChecks size={20} className="text-orange-400" />
            {editing ? "EDIT TASK" : "NEW TASK"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">TITLE</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Buy new SS 6mm rod"
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5"
              data-testid="task-title-input"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">DESCRIPTION (OPTIONAL)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Details, remarks, koi note…"
              rows={3}
              className="bg-slate-800/60 border-slate-600 text-white rounded-sm mt-1.5"
              data-testid="task-desc-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">PRIORITY</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5" data-testid="task-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  <SelectItem value="high">🔥 High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">DUE DATE (OPTIONAL)</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5"
                data-testid="task-due-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">ASSIGN TO</Label>
            <Select value={assigneeId} onValueChange={setAssigneeId} disabled={!isAdmin && !editing}>
              <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5" data-testid="task-assignee-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-600 text-white">
                <SelectItem value="__none__">— Koi bhi (Unassigned) —</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} · {(u.role || "").replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isAdmin && (
              <div className="text-[10px] text-slate-500 mt-1">
                Aap sirf apne aap ko ya "Unassigned" rakh sakte ho.
              </div>
            )}
          </div>

          {/* ── ATTACHMENTS ─────────────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">
                ATTACHMENTS (PHOTO / PDF · MAX 15 MB EACH)
              </Label>
              {uploadingCount > 0 && (
                <span className="text-[10px] font-mono-num text-cyan-300 animate-pulse">
                  Uploading {uploadingCount}…
                </span>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/gif,application/pdf"
              onChange={onPickFiles}
              data-testid="task-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-1.5 w-full h-11 rounded-sm border-2 border-dashed border-slate-700 hover:border-orange-500/60 hover:bg-orange-500/5 text-slate-300 hover:text-orange-300 flex items-center justify-center gap-2 transition"
              data-testid="task-file-pick-btn"
            >
              <Paperclip size={16} weight="bold" />
              <span className="font-mono-num text-xs tracking-widest">TAP TO ATTACH FILES</span>
            </button>

            {/* Existing (already uploaded) attachments */}
            {existing.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="task-existing-attachments">
                {existing.map((a) => (
                  <AttachmentChip
                    key={a.id}
                    att={a}
                    taskId={editing?.id}
                    canDelete={user?.role === "admin" || user?.role === "secondary_admin" || a.uploaded_by === user?.email || editing?.created_by === user?.email}
                    onDelete={() => removeExisting(a)}
                  />
                ))}
              </div>
            )}

            {/* Queued (not yet saved) — only for NEW task path */}
            {queuedFiles.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="task-queued-attachments">
                {queuedFiles.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-2 rounded-sm bg-slate-800/60 border border-slate-700"
                  >
                    {f.type === "application/pdf"
                      ? <FilePdf size={16} className="text-red-400 shrink-0" weight="fill" />
                      : <ImageSquare size={16} className="text-cyan-400 shrink-0" weight="fill" />}
                    <span className="text-xs text-slate-200 flex-1 truncate">{f.name}</span>
                    <span className="text-[10px] font-mono-num text-slate-500">
                      {(f.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      type="button"
                      onClick={() => removeQueued(i)}
                      className="text-slate-500 hover:text-red-400"
                      data-testid={`task-queued-remove-${i}`}
                    >
                      <XIcon size={14} />
                    </button>
                  </div>
                ))}
                <div className="text-[10px] text-slate-500">
                  Task save hone ke baad yeh files upload ho jaayengi.
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800 h-11"
            >
              {editing ? "Close" : "Cancel"}
            </Button>
            <Button
              type="submit"
              disabled={busy || uploadingCount > 0}
              className="btn-industrial rounded-sm h-11 px-6 font-heading tracking-widest"
              data-testid="task-submit"
            >
              {busy ? "SAVING…" : (editing ? "SAVE" : "ADD TASK")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------- Attachment chip (view / download / delete) ---------------- */
function AttachmentChip({ att, taskId, canDelete, onDelete }) {
  const token = typeof window !== "undefined" ? (localStorage.getItem("sc_token") || "") : "";
  const url = taskId
    ? `${API}/tasks/${taskId}/attachments/${att.id}?auth=${encodeURIComponent(token)}`
    : "";
  const isImage = (att.content_type || "").startsWith("image/");
  const isPdf = att.content_type === "application/pdf";
  return (
    <div
      className="flex items-center gap-2 p-2 rounded-sm bg-slate-800/60 border border-slate-700"
      data-testid={`task-attachment-${att.id}`}
    >
      {isPdf
        ? <FilePdf size={18} className="text-red-400 shrink-0" weight="fill" />
        : <ImageSquare size={18} className="text-cyan-400 shrink-0" weight="fill" />}
      <span className="text-xs text-slate-200 flex-1 truncate">{att.original_filename}</span>
      <span className="text-[10px] font-mono-num text-slate-500 shrink-0">
        {((att.size || 0) / 1024).toFixed(0)} KB
      </span>
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-400 hover:text-cyan-300 p-1"
          data-testid={`task-attachment-open-${att.id}`}
          title={isImage ? "View" : "Open PDF"}
        >
          <Eye size={14} />
        </a>
      )}
      {url && (
        <a
          href={url}
          download={att.original_filename}
          className="text-slate-400 hover:text-cyan-300 p-1"
          data-testid={`task-attachment-download-${att.id}`}
          title="Download"
        >
          <DownloadSimple size={14} />
        </a>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="text-slate-500 hover:text-red-400 p-1"
          data-testid={`task-attachment-remove-${att.id}`}
          title="Remove"
        >
          <XIcon size={14} />
        </button>
      )}
    </div>
  );
}
